import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { getBaseURL } from '@scoutus/sports-metrics';
import { Config } from '../Config';

/**
 * Shared axios client for admin API routes not yet in `@scoutus/sports-metrics`.
 * AuthProvider calls {@link setAdminAuthToken} whenever the Auth0 access token refreshes.
 */
const client: AxiosInstance = axios.create();

let authToken: string | null = null;

/** Prefer runtime Config (public/config.json) so local admin hits localhost. */
function resolveAdminBaseURL(): string {
  const fromConfig =
    typeof Config.API_BASE_URL === 'string' ? Config.API_BASE_URL.trim() : '';
  if (fromConfig) return fromConfig.replace(/\/$/, '');
  return (getBaseURL() || '').replace(/\/$/, '');
}

/** Called by AuthProvider whenever it refreshes the Auth0 access token. */
export function setAdminAuthToken(token: string | null): void {
  authToken = token;
}

export function getAdminAuthHeaders(): Record<string, string> {
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

client.interceptors.request.use((config) => {
  config.baseURL = resolveAdminBaseURL();
  if (authToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

export const adminAxiosClient = client;

/** The API's `{ errorCode, message }` body, or a plain message. */
export function adminAxiosErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = (
      error as AxiosError<{ message?: unknown; errorCode?: string }>
    ).response?.data;
    if (data && typeof data === 'object' && 'message' in data) {
      const message = Array.isArray(data.message)
        ? data.message.join(', ')
        : String(data.message);
      return data.errorCode ? `${message} (${data.errorCode})` : message;
    }
    if (error.request && !error.response) return 'Cannot reach the API.';
  }
  return error instanceof Error ? error.message : 'Something went wrong';
}

/** Request under `/api/v1/admin` and return response data. */
export async function adminApiRequest<T>(
  config: AxiosRequestConfig,
): Promise<T> {
  const response = await client.request<T>({
    ...config,
    url: `/api/v1/admin${config.url ?? ''}`,
  });
  return response.data;
}
